import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import type { Control, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import { PolicyAndPricingLinks } from '@/components/steps/PolicyAndPricingLinks';
import type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import { SummaryStepCardsSection } from '@/components/steps/SummaryStep/SummaryStepCardsSection';
import { SummaryStepCompanySection } from '@/components/steps/SummaryStep/SummaryStepCompanySection';
import { SummaryStepContactSection } from '@/components/steps/SummaryStep/SummaryStepContactSection';
import { SummaryStepInvoicingSection } from '@/components/steps/SummaryStep/SummaryStepInvoicingSection';
import { SummaryStepShippingSection } from '@/components/steps/SummaryStep/SummaryStepShippingSection';
import { trackSummarySubmit } from '@/features/onboarding/utils/analytics';
import { useScrollToTopWhenLoaded } from '@/hooks/useScrollToTopWhenLoaded';
import { t } from '@/lib/utils';
import { useLocaleStore } from '@/stores/localeStore';

export type SummaryStepProps = {
    chargingCardsState: ChargingCardsSchemaType;
    invoicingDataState?: InvoiceDataSchemaType;
    shippingAddressState: ShippingSchemaType;
    goToPage: (stepNumber: number) => void;
    setCallback: () => void;
    onClickPrevious: () => void;
    isLoading: boolean;
    isOrderNewCard: boolean;
    termsAndConditions?: {
        url?: string;
        englishUrl: string;
    };
};

const SummarySchema = z.object({
    termsAndConditions: z.optional(z.boolean()).refine(b => b, { message: '' }),
});
export type SummarySchemaType = z.infer<typeof SummarySchema>;

const SummaryStepTitle = () => (
    <div className='display-flex flex-column align-items-center margin-bottom-15'>
        <div className='text-bold text-size-h3 padding-bottom-5'>{t('onboarding.reviewAndSignUp.title')}</div>
        <div className='text-size-14 padding-bottom-5'>
            {t('onboarding.reviewAndSignUp.description').split('\n')[0]}
        </div>
        <div className='text-size-14'>{t('onboarding.reviewAndSignUp.description').split('\n')[1]}</div>
    </div>
);

type SummaryStepTermsAndConditionsProps = {
    errors: FieldErrors<SummarySchemaType>;
    control: Control<SummarySchemaType>;
    termsAndConditions?: {
        url?: string;
        englishUrl: string;
    };
};
const SummaryStepTermsAndConditions = ({ errors, control, termsAndConditions }: SummaryStepTermsAndConditionsProps) => {
    const { displayLocale } = useLocaleStore();
    const isEnglish = displayLocale?.startsWith('en');
    const showEnglishVersion = !isEnglish && !!termsAndConditions?.url;
    const localLanguageUrl = showEnglishVersion
        ? (termsAndConditions.url ?? '')
        : (termsAndConditions?.englishUrl ?? '');

    return (
        <>
            <div
                className={`display-flex flex-column justify-content-center rounded bg-white 
      padding-25 ${errors.termsAndConditions ? 'border-color-danger border' : 'margin-bottom-15'}`}
            >
                <div className='form-group margin-bottom-0'>
                    <Controller
                        name='termsAndConditions'
                        control={control}
                        render={({ field }) => (
                            <div className='display-flex gap-5'>
                                <Checkbox onChange={field.onChange} checked={field.value} />
                                <span className='text-size-14'>
                                    <FormattedMessage
                                        id='onboarding.confirmTermsAndConditions.localLanguage'
                                        values={{
                                            link: msg => (
                                                <a
                                                    className='text-color-primary'
                                                    href={localLanguageUrl}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                >
                                                    {msg}
                                                </a>
                                            ),
                                        }}
                                    />{' '}
                                    {showEnglishVersion && (
                                        <FormattedMessage
                                            id='onboarding.confirmTermsAndConditions.englishVersion'
                                            values={{
                                                link: msg => (
                                                    <a
                                                        className='text-color-primary'
                                                        href={termsAndConditions.englishUrl}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                    >
                                                        {msg}
                                                    </a>
                                                ),
                                            }}
                                        />
                                    )}
                                </span>
                            </div>
                        )}
                    />
                </div>
            </div>
            {!!errors.termsAndConditions && (
                <div className='width-400 text-size-12 bg-danger help-block rounded margin-bottom-20'>
                    <div className='padding-x-5'>{t('onboarding.mustAcceptTermsAndConditions')}</div>
                </div>
            )}
        </>
    );
};

type SummaryStepActionButtonsProps = {
    onClickPrevious: () => void;
    isLoading: boolean;
    setCallback: () => void;
    handleSubmit: UseFormHandleSubmit<SummarySchemaType>;
    isOrderNewCard: boolean;
};
const SummaryStepActionButtons = ({
    onClickPrevious,
    isLoading,
    handleSubmit,
    setCallback,
    isOrderNewCard,
}: SummaryStepActionButtonsProps) => (
    <div className='btn-toolbar justify-content-between'>
        <Button className='width-100' onClick={onClickPrevious}>
            <span className='uikit-icon icon-chevron-left text-bold text-size-14 padding-right-5' />
            <span>{t('onboarding.button.back')}</span>
        </Button>
        <Button
            className={`${isLoading ? 'btn-loading' : ''}`}
            onClick={handleSubmit(() => setCallback())}
            bsStyle='primary'
            disabled={isLoading}
        >
            <span className='padding-right-5'>
                {isOrderNewCard ? t('chargingCards.newOrder.confirm') : t('onboarding.button.signup')}
            </span>
            <span className='uikit-icon icon-chevron-right text-bold text-size-14' />
        </Button>
    </div>
);

const SummaryStep = ({
    chargingCardsState,
    invoicingDataState,
    shippingAddressState,
    goToPage,
    setCallback,
    onClickPrevious,
    isLoading,
    isOrderNewCard,
    termsAndConditions,
}: SummaryStepProps) => {
    useScrollToTopWhenLoaded(isOrderNewCard);
    const { displayLocale } = useLocaleStore();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<SummarySchemaType>({
        defaultValues: {
            termsAndConditions: isOrderNewCard,
        },
        mode: 'all',
        resolver: zodResolver(SummarySchema),
    });

    const handleSummarySubmit = () => {
        const addressType = shippingAddressState.addressType;
        const hasRecipient = Boolean(shippingAddressState.addressee);
        trackSummarySubmit(isOrderNewCard, hasRecipient, addressType);
        setCallback();
    };

    if (!isOrderNewCard) {
        return (
            <div className='display-flex flex-column'>
                <SummaryStepTitle />
                <SummaryStepCardsSection chargingCardsState={chargingCardsState} goToPage={goToPage} />
                <SummaryStepCompanySection invoicingDataState={invoicingDataState!} goToPage={goToPage} />
                <SummaryStepContactSection
                    invoicingDataState={invoicingDataState!}
                    goToPage={goToPage}
                    displayLocale={displayLocale}
                />
                <SummaryStepInvoicingSection invoicingDataState={invoicingDataState!} goToPage={goToPage} />
                <SummaryStepShippingSection
                    shippingAddressState={shippingAddressState}
                    goToPage={goToPage}
                    isOrderNewCard={isOrderNewCard}
                />
                <SummaryStepTermsAndConditions
                    errors={errors}
                    control={control}
                    termsAndConditions={termsAndConditions}
                />
                <SummaryStepActionButtons
                    onClickPrevious={onClickPrevious}
                    handleSubmit={handleSubmit}
                    isLoading={isLoading}
                    setCallback={handleSummarySubmit}
                    isOrderNewCard={isOrderNewCard}
                />
                <PolicyAndPricingLinks />
            </div>
        );
    }

    return (
        <div className='display-flex flex-column'>
            <SummaryStepCardsSection chargingCardsState={chargingCardsState} goToPage={goToPage} className='border' />
            <SummaryStepShippingSection
                shippingAddressState={shippingAddressState}
                goToPage={goToPage}
                isOrderNewCard={isOrderNewCard}
                className='border'
            />
            <SummaryStepActionButtons
                onClickPrevious={onClickPrevious}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                setCallback={handleSummarySubmit}
                isOrderNewCard={isOrderNewCard}
            />
        </div>
    );
};

export default SummaryStep;
